import { LucideProps } from 'lucide-react';

import { StackedIcon } from '@/components/molecules/stacked-icon';
import { Card } from '@/ux-common/components/card/card';
import { Stack } from '@/ux-common/components/stack/stack';
import { Typography } from '@/ux-common/components/typography/typography';
import { cn } from '@/ux-common/lib/utils';

interface EmptyCardSectionProps {
  /** Optional action(s) rendered below the subtext (e.g. a button + help text) */
  action?: React.ReactNode;
  /** Extra classes for the Card wrapper (e.g. `flex-1` to fill available height) */
  className?: string;
  /** Override the `data-component` attribute (defaults to "EmptyCardSection") */
  dataComponent?: string;
  /** Override the `data-testid` attribute (defaults to "empty-card-section") */
  dataTestId?: string;
  icon: React.ComponentType<LucideProps>;
  /** Message to display (e.g., countdown or "no data") */
  message: string;
  /** Subtext with additional details */
  subtext: string;
}

/**
 * Empty state component for a card-shaped section: centered stacked icon, a
 * headline message, supporting subtext, and an optional action slot. Used for
 * "no data", "not configured", and countdown states across features. Pass
 * `dataComponent` / `dataTestId` to give a specific instance its own
 * test/analytics identity, and `action` to add a call-to-action (upload, add
 * equipment, etc.).
 */
export const EmptyCardSection = ({
  action,
  className,
  dataComponent = 'EmptyCardSection',
  dataTestId = 'empty-card-section',
  icon,
  message,
  subtext,
}: EmptyCardSectionProps) => {
  return (
    <Card
      className={cn('flex flex-col items-center justify-center py-16 px-8', className)}
      data-component={dataComponent}
      data-testid={dataTestId}
    >
      <Stack className="stack-sm items-center text-center">
        <StackedIcon icon={icon} />
        <Typography as="h4" className="text-card-header text-ckds-text-fg-default">
          {message}
        </Typography>
        <Typography className="text-body text-ckds-text-fg-tier2 max-w-md">{subtext}</Typography>
        {action}
      </Stack>
    </Card>
  );
};
